import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Full-screen obsidian loader shown on first app load.
 * Assembles sharp shards into a crystal, then "shatters" away.
 */
const SHARDS = [
  { id: 1, className: "shard-1", x: "-40%", y: "-20%", rot: -20 },
  { id: 2, className: "shard-2", x: "30%", y: "-30%", rot: 15 },
  { id: 3, className: "shard-3", x: "-30%", y: "30%", rot: 10 },
  { id: 4, className: "shard-1", x: "40%", y: "25%", rot: -25 },
  { id: 5, className: "shard-2", x: "0%", y: "0%", rot: 0 },
];

export const InitialLoader = ({ onComplete }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onComplete && onComplete(), 900);
    }, 2600);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="initial-loader"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050505] noise-overlay"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
        >
          {/* Crystal assembling */}
          <div className="relative w-64 h-64 flex items-center justify-center">
            {SHARDS.map((s, i) => (
              <motion.div
                key={s.id}
                className={`absolute w-32 h-32 ${s.className}`}
                style={{
                  background:
                    "linear-gradient(135deg, #8B5CF6 0%, #1A0A2E 60%, #6D28D9 100%)",
                  boxShadow: "inset 0 0 40px rgba(196,181,253,0.15), 0 0 30px rgba(139,92,246,0.25)",
                }}
                initial={{
                  x: s.x,
                  y: s.y,
                  rotate: s.rot + 180,
                  opacity: 0,
                  scale: 0.4,
                }}
                animate={{
                  x: "0%",
                  y: "0%",
                  rotate: s.rot,
                  opacity: 1,
                  scale: 1,
                }}
                exit={{
                  x: s.x,
                  y: s.y,
                  rotate: s.rot + 90,
                  opacity: 0,
                  scale: 0.3,
                }}
                transition={{
                  duration: 1.6,
                  delay: i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            ))}
          </div>

          {/* Wordmark */}
          <motion.div
            className="absolute bottom-20 left-1/2 -translate-x-1/2 text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
          >
            <div className="font-display text-2xl md:text-3xl font-black tracking-[0.3em] text-violet-glow">
              OBSIDIAN
            </div>
            <div className="text-xs tracking-[0.5em] text-violet-300/60 mt-2">
              MEDIA
            </div>
          </motion.div>

          {/* Progress line */}
          <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2 h-[2px] bg-violet-400"
            initial={{ width: 0 }}
            animate={{ width: 160 }}
            transition={{ duration: 2.4, ease: "linear" }}
            style={{ boxShadow: "0 0 20px rgba(139,92,246,0.7)" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InitialLoader;
